import React, { useEffect, useRef } from 'react';
import { GeoJSON, useMapEvents } from 'react-leaflet';
import { stoneSeenLabel } from '../../fields.utils';
import { createStoneMarker, restyleStoneMarker } from './stoneMarker';

const stateOf = ({ id }, { selectedId, listedIds, colorBy, mustPickCm }) => ({
  isSelected: id === selectedId,
  isListed: !listedIds || listedIds.has(id),
  colorBy,
  mustPickCm
});

const element = (tag, className, text) => {
  const node = document.createElement(tag);
  node.className = className;
  node.textContent = text;
  return node;
};

const tooltipOf = stone => {
  const root = document.createElement('div');
  const title = element(
    'b',
    'stone-tip-title',
    stone.size_cm == null ? 'Stone, size not known' : `${stone.size_cm} cm stone`
  );
  root.appendChild(title);
  if (stone.size_cm != null) {
    root.appendChild(
      element(
        'span',
        'stone-tip-est',
        stone.size_is_mocked ? ' mocked size' : ' estimated'
      )
    );
  }
  root.appendChild(element('div', 'stone-tip-row', `Stone #${stone.id}`));
  const seen = stoneSeenLabel(stone);
  if (seen) {
    root.appendChild(element('div', 'stone-tip-row', `Seen on ${seen}`));
  }
  return root;
};

const layerKey = features =>
  `stones-${features.length}-${features.reduce(
    (sum, { properties }) => sum + properties.id,
    0
  )}`;

export const StoneLayer = ({
  stones,
  selectedId,
  listedIds,
  colorBy,
  mustPickCm,
  onSelect
}) => {
  const group = useRef();
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const view = { selectedId, listedIds, colorBy, mustPickCm };

  useMapEvents({ click: () => onSelectRef.current(null) });

  useEffect(() => {
    group.current?.eachLayer(layer => {
      const stone = layer.feature.properties;
      restyleStoneMarker(layer, stone, stateOf(stone, view));
    });
  }, [selectedId, listedIds, stones, colorBy, mustPickCm]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <GeoJSON
      ref={group}
      key={layerKey(stones.features)}
      data={stones}
      pointToLayer={(feature, latlng) =>
        createStoneMarker(latlng, feature.properties, stateOf(feature.properties, view))
      }
      onEachFeature={(feature, layer) => {
        layer.bindTooltip(tooltipOf(feature.properties), {
          sticky: true,
          direction: 'auto',
          offset: [14, -18],
          className: 'stone-tip'
        });
        layer.on('click', () => onSelectRef.current(feature.properties.id));
      }}
    />
  );
};
